import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Label } from '../../../../../../../common/labels/labelTypes';
import { UseSheetReturnType } from '../../../../../../mobile/structural/sheet/hooks/useSheet';
import { useLabelColorHandler } from './useLabelColorHandler';
import type CustomColorInput from '../../colorTools/CustomColorInput';

type UseLabelColorSheetHandlerReturnType = {
    selectedColor: string | undefined;
    selectedColorHex: string;
    showCustomColorInput: boolean;
    customColorInputRef: RefObject<CustomColorInput>;
    handleTempColorChange: (color: string | null) => void;
    handleColorChange: (color: string | null) => void;
    openCustomColorPicker: () => void;
    onCustomColorInputFocus: () => void;
    onCustomColorInputBlur: () => void;
};

export const useLabelColorSheetHandler = (
    label: Label | undefined,
    sheetProps: UseSheetReturnType,
): UseLabelColorSheetHandlerReturnType => {
    const customColorInputRef = useRef<CustomColorInput>(null);
    const [isCustomColorPickerOpen, setIsCustomColorPickerOpen] = useState(false);

    const { handleTempColorChange, handleColorChange, selectedColor, selectedColorHex, clearPreview } =
        useLabelColorHandler(label);

    useEffect(() => {
        if (isCustomColorPickerOpen || sheetProps.isOpen) return;

        clearPreview();
    }, [clearPreview, isCustomColorPickerOpen, sheetProps.isOpen]);

    const openCustomColorPicker = useCallback(() => {
        customColorInputRef.current?.open();
    }, []);

    const onCustomColorInputFocus = () => {
        setIsCustomColorPickerOpen(true);
    };

    const onCustomColorInputBlur = () => {
        setIsCustomColorPickerOpen(false);
    };

    const showCustomColorInput = sheetProps.isOpen || isCustomColorPickerOpen;

    return {
        selectedColor,
        selectedColorHex,
        showCustomColorInput,
        customColorInputRef,
        handleTempColorChange,
        handleColorChange,
        openCustomColorPicker,
        onCustomColorInputFocus,
        onCustomColorInputBlur,
    };
};
